import React from 'react';

const TouchButton = ({ 
  children, 
  variant = 'primary', 
  size = 'medium', 
  fullWidth = false, 
  disabled = false, 
  loading = false,
  icon,
  onClick, 
  className = '',
  ...props 
}) => {
  const baseStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontFamily: 'inherit',
    fontWeight: '600',
    textAlign: 'center',
    textDecoration: 'none',
    border: 'none',
    borderRadius: '8px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 150ms ease-in-out',
    outline: 'none',
    userSelect: 'none',
    webkitUserSelect: 'none',
    WebkitTapHighlightColor: 'transparent',
    position: 'relative',
    overflow: 'hidden',
    // Touch-friendly minimum size
    minHeight: '44px',
    minWidth: '44px',
    // Better touch target
    touchAction: 'manipulation',
    // Prevent double-tap zoom
    webkitTouchCallout: 'none',
    // Prevent text selection
    webkitUserSelect: 'none',
    khtmlUserSelect: 'none',
    mozUserSelect: 'none',
    msUserSelect: 'none',
    userSelect: 'none'
  };

  const variants = {
    primary: {
      backgroundColor: disabled ? '#ccc' : '#2196F3',
      color: '#ffffff',
      boxShadow: disabled ? 'none' : '0 2px 4px rgba(33, 150, 243, 0.2)',
      '&:hover': !disabled && {
        backgroundColor: '#1976D2',
        boxShadow: '0 4px 8px rgba(33, 150, 243, 0.3)'
      },
      '&:active': !disabled && {
        backgroundColor: '#1565C0',
        transform: 'translateY(1px)'
      }
    },
    secondary: {
      backgroundColor: disabled ? '#f5f5f5' : '#ffffff',
      color: disabled ? '#ccc' : '#2196F3',
      border: `1px solid ${disabled ? '#ccc' : '#2196F3'}`,
      '&:hover': !disabled && {
        backgroundColor: '#f5f5f5'
      },
      '&:active': !disabled && {
        backgroundColor: '#e0e0e0'
      }
    },
    success: {
      backgroundColor: disabled ? '#ccc' : '#4CAF50',
      color: '#ffffff',
      boxShadow: disabled ? 'none' : '0 2px 4px rgba(76, 175, 80, 0.2)',
      '&:hover': !disabled && {
        backgroundColor: '#45a049'
      },
      '&:active': !disabled && {
        backgroundColor: '#3d8b40'
      }
    },
    warning: {
      backgroundColor: disabled ? '#ccc' : '#FF9800',
      color: '#ffffff',
      boxShadow: disabled ? 'none' : '0 2px 4px rgba(255, 152, 0, 0.2)',
      '&:hover': !disabled && {
        backgroundColor: '#f57c00'
      },
      '&:active': !disabled && {
        backgroundColor: '#ef6c00'
      }
    },
    error: {
      backgroundColor: disabled ? '#ccc' : '#F44336',
      color: '#ffffff',
      boxShadow: disabled ? 'none' : '0 2px 4px rgba(244, 67, 54, 0.2)',
      '&:hover': !disabled && {
        backgroundColor: '#d32f2f'
      },
      '&:active': !disabled && {
        backgroundColor: '#c62828'
      }
    },
    outline: {
      backgroundColor: 'transparent',
      color: disabled ? '#ccc' : '#2196F3',
      border: `2px solid ${disabled ? '#ccc' : '#2196F3'}`,
      '&:hover': !disabled && {
        backgroundColor: 'rgba(33, 150, 243, 0.1)'
      },
      '&:active': !disabled && {
        backgroundColor: 'rgba(33, 150, 243, 0.2)'
      }
    },
    ghost: {
      backgroundColor: 'transparent',
      color: disabled ? '#ccc' : '#2196F3',
      border: 'none',
      '&:hover': !disabled && {
        backgroundColor: 'rgba(33, 150, 243, 0.1)'
      },
      '&:active': !disabled && {
        backgroundColor: 'rgba(33, 150, 243, 0.2)'
      }
    }
  };

  const sizes = {
    small: {
      padding: '8px 16px',
      fontSize: '14px',
      minHeight: '36px'
    },
    medium: {
      padding: '12px 24px',
      fontSize: '16px',
      minHeight: '44px'
    },
    large: {
      padding: '16px 32px',
      fontSize: '18px',
      minHeight: '52px'
    }
  };

  const buttonStyles = {
    ...baseStyles,
    ...variants[variant],
    ...sizes[size],
    width: fullWidth ? '100%' : 'auto',
    opacity: disabled ? 0.6 : 1
  };

  const handleClick = (e) => {
    if (disabled || loading) {
      e.preventDefault();
      return;
    }
    
    // Add ripple effect with performance optimization
    const button = e.currentTarget;
    
    // Use requestAnimationFrame to avoid forced reflow
    requestAnimationFrame(() => {
      const rect = button.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height);
      const x = e.clientX - rect.left - size / 2;
      const y = e.clientY - rect.top - size / 2;
      
      const ripple = document.createElement('span');
      ripple.style.cssText = `
        position: absolute;
        border-radius: 50%;
        transform: scale(0);
        animation: ripple 600ms linear;
        background-color: rgba(255, 255, 255, 0.6);
        width: ${size}px;
        height: ${size}px;
        left: ${x}px;
        top: ${y}px;
        pointer-events: none;
      `;
      
      button.appendChild(ripple);
      
      setTimeout(() => {
        if (ripple && ripple.parentNode) {
          ripple.remove();
        }
      }, 600);
    });
    
    if (onClick) {
      onClick(e);
    }
  };

  return (
    <button
      style={buttonStyles}
      onClick={handleClick}
      disabled={disabled || loading}
      className={className}
      {...props}
    >
      {loading && (
        <span style={{
          display: 'inline-block',
          width: '16px',
          height: '16px',
          border: '2px solid currentColor',
          borderTop: '2px solid transparent',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
      )}
      {icon && !loading && (
        <span style={{ display: 'flex', alignItems: 'center' }}>
          {icon}
        </span>
      )}
      {children}
      
      <style jsx>{`
        @keyframes ripple {
          to {
            transform: scale(4);
            opacity: 0;
          }
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </button>
  );
};

export default TouchButton;